import { useState } from "react";
import { Plus, Minus, Check, Layers, Tag, Heart } from "lucide-react";
import { useCart } from "@/App";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import ProductQuickView from "./ProductQuickView";
import useRecentlyViewed from "@/hooks/useRecentlyViewed";
import useWishlist from "@/hooks/useWishlist";

const ADDED_FEEDBACK_DURATION = 1500;

export default function ProductCard({ product }) {
  const { addToCart, cart, variantCounts, variantSavings, unitPrices } = useCart();
  const { addId: trackView } = useRecentlyViewed();
  const { has: isFavorite, toggle: toggleFavorite } = useWishlist();
  const variantCount = variantCounts?.[product.id] || 0;
  const savePct = variantSavings?.[product.id] || 0;
  const unitPrice = unitPrices?.[product.id] || null;
  const [selectedWeight, setSelectedWeight] = useState(
    product.weight_options?.[0] || null
  );
  const [quantity, setQuantity] = useState(1);
  const [justAdded, setJustAdded] = useState(false);
  const [quickViewOpen, setQuickViewOpen] = useState(false);

  const displayPrice = product.is_special && product.special_price 
    ? product.special_price 
    : product.price;

  const inCart = cart.find(
    (item) => item.product_id === product.id && item.weight_option === selectedWeight
  );

  const handleAddToCart = () => {
    addToCart(product, quantity, selectedWeight);
    setJustAdded(true);
    toast.success(`${product.name} added to cart!`);
    setTimeout(() => setJustAdded(false), ADDED_FEEDBACK_DURATION);
    setQuantity(1);
  };

  return (
    <div 
      className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm product-card"
      data-testid={`product-card-${product.id}`}
    >
      {/* Image */}
      <div
        className="relative aspect-[4/3] overflow-hidden bg-slate-100 cursor-pointer"
        onClick={() => {
          trackView(product.id);
          setQuickViewOpen(true);
        }}
        data-testid={`product-image-${product.id}`}
      >
        {product.image_url ? (
          <img
            src={product.image_url}
            alt={product.name}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-slate-400">
            No image
          </div>
        )}
        
        {/* Special badge */}
        {product.is_special && (
          <Badge 
            className="absolute top-3 left-3 bg-red-600 text-white special-badge"
            data-testid={`special-badge-${product.id}`}
          >
            SPECIAL OFFER
          </Badge>
        )}

        {/* Best-value (per-unit savings) badge */}
        {savePct > 0 && product.stock > 0 && (
          <Badge
            className={`absolute ${product.is_special ? "top-12" : "top-3"} left-3 bg-amber-500 hover:bg-amber-500 text-white border-0 shadow-md flex items-center gap-1`}
            data-testid={`save-badge-${product.id}`}
          >
            <Tag className="h-3 w-3" />
            Save {savePct}%
          </Badge>
        )}

        {/* Stock indicator */}
        {product.stock < 10 && product.stock > 0 && (
          <Badge 
            variant="outline" 
            className="absolute top-3 right-3 bg-white/90 text-amber-600 border-amber-200"
          >
            Only {product.stock} left
          </Badge>
        )}

        {/* Wishlist heart button — top-right (below "Only X left" badge if present) */}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            const added = toggleFavorite(product.id);
            toast[added ? "success" : "message"](
              added ? "Added to Wishlist" : "Removed from Wishlist"
            );
          }}
          aria-label={isFavorite(product.id) ? "Remove from wishlist" : "Add to wishlist"}
          className={`absolute ${
            product.stock < 10 && product.stock > 0 ? "top-12" : "top-3"
          } right-3 h-9 w-9 rounded-full bg-white/95 hover:bg-white shadow-md flex items-center justify-center transition-transform hover:scale-110`}
          data-testid={`wishlist-btn-${product.id}`}
        >
          <Heart
            className={`h-4 w-4 transition-colors ${
              isFavorite(product.id)
                ? "fill-rose-500 text-rose-500"
                : "text-slate-500"
            }`}
          />
        </button>

        {product.stock === 0 && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
            <Badge variant="destructive" className="text-base">Out of Stock</Badge>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-4">
        <h3
          className="font-semibold text-slate-900 text-lg leading-tight mb-1 cursor-pointer hover:text-emerald-600 transition-colors"
          onClick={() => {
            trackView(product.id);
            setQuickViewOpen(true);
          }}
          data-testid={`product-title-${product.id}`}
        >
          {product.name}
        </h3>
        {product.description && (
          <p className="text-sm text-slate-500 mb-3 line-clamp-2">{product.description}</p>
        )}

        {/* Variant availability badge */}
        {variantCount > 0 && (
          <button
            type="button"
            onClick={() => setQuickViewOpen(true)}
            className="inline-flex items-center gap-1.5 mb-3 px-2.5 py-1 text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full hover:bg-emerald-100 transition-colors"
            data-testid={`variant-badge-${product.id}`}
          >
            <Layers className="h-3 w-3" />
            Also available in {variantCount} other size{variantCount > 1 ? "s" : ""}
          </button>
        )}

        {/* Price */}
        <div className="flex items-baseline gap-2 mb-1">
          <span className="text-xl font-bold text-emerald-600" data-testid={`price-${product.id}`}>
            £{displayPrice.toFixed(2)}
          </span>
          <span className="text-sm text-slate-500">/ {product.unit}</span>
          {product.is_special && product.special_price && (
            <span className="text-sm text-slate-400 line-through">
              £{product.price.toFixed(2)}
            </span>
          )}
        </div>
        {/* Per-unit price (£/kg, £/100g, £/L, £/100ml) */}
        {unitPrice && (
          <p className="text-xs text-slate-500 mb-3" data-testid={`unit-price-${product.id}`}>
            {unitPrice}
          </p>
        )}
        {!unitPrice && <div className="mb-3" />}

        {/* Weight selector if applicable */}
        {product.weight_options && product.weight_options.length > 0 && (
          <div className="mb-3">
            <Select value={selectedWeight} onValueChange={setSelectedWeight}>
              <SelectTrigger 
                className="w-full"
                data-testid={`weight-select-${product.id}`}
              >
                <SelectValue placeholder="Select weight" />
              </SelectTrigger>
              <SelectContent>
                {product.weight_options.map((weight) => (
                  <SelectItem key={weight} value={weight}>
                    {weight}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Quantity and Add button */}
        <div className="flex items-center gap-2">
          {/* Quantity selector */}
          <div className="flex items-center border border-slate-200 rounded-lg">
            <button
              onClick={() => setQuantity(Math.max(1, quantity - 1))}
              className="w-9 h-9 flex items-center justify-center text-slate-600 hover:text-emerald-600 qty-btn"
              data-testid={`qty-decrease-${product.id}`}
            >
              <Minus className="h-4 w-4" />
            </button>
            <span className="w-8 text-center font-medium">{quantity}</span>
            <button
              onClick={() => setQuantity(quantity + 1)}
              className="w-9 h-9 flex items-center justify-center text-slate-600 hover:text-emerald-600 qty-btn"
              data-testid={`qty-increase-${product.id}`}
            >
              <Plus className="h-4 w-4" />
            </button>
          </div>

          {/* Add to cart button */}
          <Button
            onClick={handleAddToCart}
            disabled={product.stock === 0}
            className={`flex-1 h-9 rounded-lg font-medium transition-all ${
              justAdded 
                ? "bg-emerald-600 text-white" 
                : "bg-emerald-600 hover:bg-emerald-700 text-white"
            }`}
            data-testid={`add-to-cart-${product.id}`}
          >
            {justAdded ? (
              <>
                <Check className="h-4 w-4 mr-1" /> Added
              </>
            ) : (
              <>
                <Plus className="h-4 w-4 mr-1" /> Add
              </>
            )}
          </Button>
        </div>

        {/* In cart indicator */}
        {inCart && !justAdded && (
          <p className="text-sm text-emerald-600 mt-2 text-center">
            {inCart.quantity} in cart
          </p>
        )}
      </div>

      <ProductQuickView
        product={product}
        open={quickViewOpen}
        onOpenChange={setQuickViewOpen}
      />
    </div>
  );
}
